import React, { useState, useEffect, useRef, useCallback } from 'react'
import axios from 'axios'
import Slider from 'react-slick'
import TimeTop from '../banners/TimeTop'
import WebFont from 'webfontloader'
import styles from './WhatsOn.module.scss'
import Spinner from '../layout/Spinner'

// Custom hook for handling intervals
const useInterval = (callback, delay) => {
  const savedCallback = useRef()

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    const tick = () => {
      if (typeof savedCallback.current === 'function') {
        savedCallback.current()
      }
    }

    if (delay !== null) {
      // Set up the interval
      const id = setInterval(tick, delay)
      return () => clearInterval(id) // Cleanup on unmount or delay change
    }
  }, [delay])
}

const WhatsOn = ({ data, fullscreen }) => {
  // Slick carousel settings
  const sliderRef = useRef()

  const handleAfterChange = (currentSlide) => {
    const { slidesToShow } = sliderRef.current.props
    const slideCount = React.Children.count(sliderRef.current.props.children)

    if (currentSlide === slideCount - slidesToShow) {
      setTimeout(() => {
        sliderRef.current.slickGoTo(0)
      }, 4000)
    }
  }

  const sliderSettings = {
    activeSlide: 0,
    //adaptiveHeight: true,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 6000,
    centerMode: false,
    dots: false,
    draggable: false,
    infinite: false,
    pauseOnHover: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    vertical: true,
    afterChange: handleAfterChange,
  }

  const [loading, setLoading] = useState(true) // Loading state
  const [whatson, setWhatsOn] = useState([]) // Data state
  const [connectionLost, setConnectionLost] = useState(false) // Connection state

  const backgroundColor =
    data.field_background_colour && data.field_background_colour.color

  const color = data.field_text_colour && data.field_text_colour.color

  const fontFamily = data.field_font.field_font_family
    ? data.field_font.field_font_family
    : 'Work+Sans'

  const fontWeight = data.field_font.field_font_weight
  const fontStyle = data.field_font.field_font_style
  const letterSpacing = data.field_font.field_font_letter_spacing
  const textTransform = data.field_font.field_font_text_transform
  const heading = data.field_external_data.field_heading
  const endpoint = data.field_external_data.field_endpoint

  useEffect(() => {
    WebFont.load({
      google: {
        families: [fontFamily],
      },
    })
  }, [fontFamily])

  const style = {
    //backgroundImage: `url("/img/bgtest.jpg")`,
    backgroundColor,
    color,
    fontFamily,
    fontWeight,
    fontStyle,
    letterSpacing,
    textTransform,
  }

  // Function to fetch "What's On" data
  const getWhatsOn = useCallback(async () => {
    try {
      const d = Math.floor(Date.now() / 1000)
      const cachedData = localStorage.getItem('whatsOnData') // Try to get cached data

      if (cachedData) {
        // If cached data exists, use it while fetching new data in the background
        setWhatsOn(JSON.parse(cachedData))
      }

      // Fetch new data from the API
      const response = await axios.get(
        //`${apiUrl}api/node/whatson?_format=json&time_stamp=${d}`
        `${endpoint}?_format=json&time_stamp=${d}`
      )

      // Update the data state, clear loading state, and save to local storage
      setWhatsOn(response.data)

      setConnectionLost(false)
      setLoading(false)

      localStorage.setItem('whatsOnData', JSON.stringify(response.data))
    } catch (error) {
      console.error('Error fetching data:', error)

      setConnectionLost(true)

      // Since there was an error, try to use cached data if available
      const cachedData = localStorage.getItem('whatsOnData')
      if (cachedData) {
        setWhatsOn(JSON.parse(cachedData))
      }

      setLoading(false)
    }
  }, [endpoint, setWhatsOn, setConnectionLost, setLoading])

  useEffect(() => {
    // Fetch "What's On" data on component mount
    getWhatsOn()
  }, [getWhatsOn])

  // Use the custom interval hook to fetch data periodically every 10 seconds
  useInterval(() => {
    getWhatsOn()
  }, 10000)

  // Map the `whatson` array to a variable
  const whatsonItems = whatson.map((event, index) => (
    <div
      className={
        fullscreen ? styles.whatsonfullscreen__item : styles.whatson__item
      }
      key={index}
    >
      <div
        className={
          fullscreen ? styles.whatsonfullscreen__time : styles.whatson__time
        }
      >
        {event.start}
        {event.end}
      </div>
      <div
        className={
          fullscreen ? styles.whatsonfullscreen__title : styles.whatson__title
        }
      >
        {event.title}
      </div>
      <div
        className={
          fullscreen
            ? styles.whatsonfullscreen__description
            : styles.whatson__description
        }
      >
        {event.description}
      </div>
      <div
        className={
          fullscreen ? styles.whatsonfullscreen__image : styles.whatson__image
        }
      >
        {event.image && <img src={event.image} alt='' />}

        {/*<img src='https://picsum.photos/600/600' alt='' />*/}
        {/* {fullscreen && ( */}
        {/* <img
          src='https://fastly.picsum.photos/id/459/600/600.jpg?hmac=L2j4az7IO9cBtLp-u2QEPcRyl4IsJQgE7iWXb0t-xoY'
          alt=''
        /> */}
        {/* )} */}
      </div>
    </div>
  ))

  return (
    <div
      className={fullscreen ? styles.whatsonfullscreen : styles.whatson}
      style={style}
    >
      <header>
        <TimeTop />
        {heading && <h2> {heading}</h2>}
        {connectionLost && (
          <div className={styles.connectionlost}>
            Please note: This list may temporarily be out of date. Please check
            with reception.
          </div>
        )}
      </header>

      {loading ? (
        <Spinner />
      ) : (
        <div
          className={
            fullscreen ? styles.whatsonfullscreen__items : styles.whatson__items
          }
        >
          {fullscreen && whatsonItems}

          {!fullscreen && (
            <Slider ref={sliderRef} {...sliderSettings}>
              {whatsonItems}
            </Slider>
          )}
        </div>
      )}
    </div>
  )
}

export default WhatsOn
